<template>
  <div class="view-mode-selector-parts">
    <div
      class="view-mode-selector-parts__select"
      :class="{ 'view-mode-selector-parts__select--current': rankingViewModeListEnabled }"
      @click="enabledViewModeSelect"
    >
      <span class="view-mode-selector-parts__select__label">{{
        $t(`RaceVideoPage.RankingViewModeText.${selectMode}`)
      }}</span>

      <svg
        class="view-mode-selector-parts__select__icon"
        width="18"
        height="18"
        viewBox="0 0 18 18"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M1.93945 6L7.76268 11.8232C8.44609 12.5066 9.55413 12.5066 10.2375 11.8232L16.0608 6H13.9395L9.17689 10.7626C9.07926 10.8602 8.92097 10.8602 8.82334 10.7626L4.06077 6H1.93945Z"
          fill="white"
        />
      </svg>
    </div>

    <div
      class="view-mode-selector-parts__mask"
      :class="{ 'view-mode-selector-parts__mask--current': rankingViewModeListEnabled }"
      @click="changeFilter()"
    />

    <div
      class="view-mode-selector-parts__list"
      :class="{ 'view-mode-selector-parts__list--current': rankingViewModeListEnabled }"
    >
      <button
        v-for="(mode, i) in rankingViewModeList"
        class="view-mode-selector-parts__list__item"
        :class="{ 'view-mode-selector-parts__list__item--current': selectMode === mode }"
        :key="i"
        @click="changeFilter(mode)"
      >
        <span>{{ $t(`RaceVideoPage.RankingViewModeText.${mode}`) }}</span>
        <img
          v-if="selectMode === mode"
          :src="require('@/assets/img/icon/icon_check_18__green.svg')"
          alt=""
        />
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from '@vue/composition-api'
import { RankingViewMode } from '@/components/RaceVideoPage/RaceVideoRankingPane.vue'

type DataType = {
  rankingViewModeListEnabled: boolean
  selectMode: string
}

/**
 * レース動画再生画面 ランキング 表示データ切り替え(セレクター) パーツコンポーネント
 */
export default defineComponent({
  name: 'ViewModeSelectorParts',
  data(): DataType {
    return {
      rankingViewModeListEnabled: false,
      selectMode: 'gap',
    }
  },
  props: {
    /**
     * 非選択時セレクト テキストデータ
     */
    rankingViewMode: {
      type: String as PropType<RankingViewMode>,
      default: '',
    },
    /**
     * フィルターリストデータ
     */
    rankingViewModeList: {
      type: Array,
      required: true,
    },
  },
  methods: {
    /**
     * セレクトカレント表示切り替え
     */
    enabledViewModeSelect(): void {
      this.rankingViewModeListEnabled = !this.rankingViewModeListEnabled
    },
    /**
     * 表示データ切り替え
     */
    changeFilter(mode: string): void {
      if (!mode) {
        this.enabledViewModeSelect()
        return
      }

      this.selectMode = mode
      this.$emit('rankingViewModeChange', this.selectMode)

      this.enabledViewModeSelect()
    },
  },
})
</script>

<style lang="scss" scoped>
.view-mode-selector-parts {
  position: relative;

  &__select {
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    justify-content: space-between;
    width: auto;
    height: 36px;
    padding: 8px 8px 8px 12px;
    background: $black20;
    @include fontSetTextMedium(13);

    font-weight: 600;
    border-radius: 4px;
    cursor: pointer;

    &__label {
      display: block;
      max-width: calc(100% - 22px);
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
      color: $white;
    }

    &__icon {
      opacity: 0.5;
    }

    &--current {
      background: $green-light;

      .view-mode-selector-parts__select {
        &__label {
          color: $black;
        }

        &__icon {
          opacity: 1;
          transform: rotate(180deg);

          path {
            fill: $black;
          }
        }
      }
    }
  }

  &__mask {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    display: block;
    width: 100%;
    z-index: 9;
    opacity: 0;
    pointer-events: none;
    transition: all ease 0.3s;

    &--current {
      opacity: 1;
      pointer-events: auto;
    }
  }

  &__list {
    position: absolute;
    top: 36px;
    right: 0;
    display: flex;
    flex-flow: column nowrap;
    align-content: flex-start;
    align-items: flex-start;
    justify-content: flex-start;
    justify-items: flex-start;
    row-gap: 1px;
    width: 240px;
    border: 1px solid $gray75;
    border-radius: 8px;
    background: $black;
    z-index: 10;
    overflow-y: auto;
    opacity: 0;
    pointer-events: none;
    transition: all ease 0.3s;
    -webkit-overflow-scrolling: touch;

    &--current {
      opacity: 1;
      pointer-events: auto;
    }

    &__item {
      flex: none;
      display: flex;
      flex-flow: row nowrap;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      min-height: 38px;
      padding: 10px 8px 10px 15px;
      color: $white;
      background: $black80;

      + .filter-select-list-parts__list__item {
        margin-top: 1px;
      }

      &--current {
        color: $green-light;
      }

      span {
        display: block;
        max-width: calc(100% - 22px);
        font-size: 13px;
        line-height: 1.38;
        font-weight: 600;
      }
    }
  }
}
</style>

<style lang="scss">
.race-video-ranking-pane {
  &--landscape-primary,
  &--landscape-secondary {
    --content-height: calc(var(--screen-height) - 44px);
    --content-font-size: min(3.2vw, calc(var(--content-height) * 0.0217));

    .view-mode-selector-parts {
      .view-mode-selector-parts__list {
        width: 22em;
      }
    }
  }
}
</style>
